import React from 'react';
import { motion } from 'framer-motion';
import { Download, MessageSquare, BarChart3, Users, Clock, Sparkles } from 'lucide-react';
import Button from '@/components/ui/Button';
import StoreButton from '@/components/ui/StoreButton';
import { COMPANY_INFO } from '@/lib/constants';

const HeroSection = () => {
  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariant = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };
  
  const floatingIcons = [
    { icon: <MessageSquare className="w-full h-full text-orcsys-400" />, top: "15%", left: "10%", delay: 0 },
    { icon: <BarChart3 className="w-full h-full text-orcsys-400" />, top: "25%", right: "15%", delay: 1 },
    { icon: <Users className="w-full h-full text-orcsys-400" />, bottom: "25%", left: "15%", delay: 2 },
    { icon: <Clock className="w-full h-full text-orcsys-400" />, bottom: "15%", right: "10%", delay: 3 },
    { icon: <Sparkles className="w-full h-full text-orcsys-400" />, top: "40%", left: "30%", delay: 1.5 }
  ];
  
  return (
    <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 pt-20 sm:pt-24 md:pt-16">
      {/* Background pattern with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50/90 to-slate-100/70"></div>
      </div>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {floatingIcons.map((item, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{
              top: item.top,
              left: item.left,
              right: item.right,
              bottom: item.bottom,
            }}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: [0, 0.7, 0],
              y: [0, -20, 0],
              scale: [0.8, 1, 0.8]
            }}
            transition={{ 
              duration: 5,
              delay: item.delay,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut"
            }}
          >
            <div className="w-14 h-14 bg-orcsys-50 rounded-xl flex items-center justify-center shadow-lg">
              {item.icon}
            </div>
          </motion.div>
        ))}
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-orcsys-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orcsys-300/20 rounded-full blur-3xl"></div>
      </div>
      
      {/* Radial gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(142,227,205,0.2)_0%,rgba(255,255,255,0)_60%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]"></div>

      {/* Content container */}
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 lg:py-20 relative z-10">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center"
          variants={containerVariant}
          initial="hidden"
          animate="visible"
        >
          {/* Device Mockup */}
          <div className="order-2 lg:order-2 flex justify-center mt-8 sm:mt-12 lg:mt-0">
            <div className="relative">
              {/* Main device */}
              <motion.div
                variants={itemVariant}
                className="relative mx-auto w-full max-w-[280px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px]"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orcsys-100 to-orcsys-200 rounded-xl blur-xl opacity-50 animate-pulse-soft"></div>
                <div className="relative bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100">
                  {/* Screen Content */}
                  <div className="aspect-[16/9] bg-gradient-to-br from-orcsys-50 to-white overflow-hidden">
                    <img 
                      src="images/collab-app-laptop.png" 
                      alt="Collab dashboard interface"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Base */}
                  <div className="h-2 sm:h-3 md:h-4 bg-slate-100 border-t border-gray-200"></div>
                </div>
              </motion.div>

              {/* Phone Mockup */}
              <motion.div 
                variants={itemVariant}
                className="absolute -bottom-2 sm:-bottom-4 md:-bottom-6 lg:-bottom-8 left-[75%] -translate-x-1/2 lg:left-auto lg:translate-x-0 lg:-right-4 md:lg:-right-8 w-20 h-40 sm:w-24 sm:h-48 md:w-32 md:h-64 lg:w-40 lg:h-80"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-orcsys-100 to-orcsys-200 rounded-xl blur-xl opacity-50 animate-pulse-soft"></div>
                  <div className="relative bg-white rounded-[1.5rem] sm:rounded-[2rem] shadow-xl overflow-hidden border border-gray-100">
                    {/* Phone Notch */}
                    <div className="h-3 sm:h-4 md:h-6 bg-slate-900 rounded-t-[1.5rem] sm:rounded-t-[2rem] flex items-center justify-center">
                      <div className="w-12 sm:w-16 md:w-20 h-2 sm:h-3 md:h-4 bg-slate-800 rounded-full"></div>
                    </div>
                    {/* Phone Screen */}
                    <div className="aspect-[9/16] bg-gradient-to-br from-orcsys-50 to-white overflow-hidden">
                      <img 
                        src="images/collab-app-mobile.jpg" 
                        alt="Collab mobile interface"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Phone Home Button */}
                    <div className="h-6 sm:h-8 md:h-12 bg-slate-100 border-t border-gray-200 flex items-center justify-center">
                      <div className="w-6 sm:w-8 md:w-12 h-1 bg-slate-300 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Text content */}
          <div className="text-center lg:text-left order-1 lg:order-1">
            <motion.h1 
              variants={itemVariant}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-3 sm:mb-4 md:mb-6 text-slate-900"
            >
              Collab: Une Relation Client{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orcsys-400 to-orcsys-600">fluide</span>
            </motion.h1>
            
            <motion.p 
              variants={itemVariant}
              className="text-base sm:text-lg md:text-xl text-slate-600 mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto lg:mx-0"
            >
              Redéfinissez vos standards de communication avec une application de gestion de la relation client dynamique et personnalisée.
            </motion.p>
            
            <motion.div 
              variants={itemVariant}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-6 sm:mb-8"
            >
              <StoreButton type="apple" url={COMPANY_INFO.collab.apple} className="w-full sm:w-auto"/>
              <StoreButton type="google" url={COMPANY_INFO.collab.google} className="w-full sm:w-auto"/>
            </motion.div>
            
            <motion.div
              variants={itemVariant}
              className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 md:gap-4"
            >
              {[
                { label: "Communication directe", icon: <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> },
                { label: "Suivi de projet", icon: <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> },
                { label: "Collaboration en temps réel", icon: <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> }
              ].map((feature, index) => (
                <div key={index} className="flex items-center bg-white/80 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm border border-slate-200">
                  {feature.icon}
                  <span className="text-xs sm:text-sm font-medium text-slate-800">{feature.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
